import { useState } from 'react';
import ProblemModal from '../delivery/ProblemModal.jsx';
import MoveStopModal from './MoveStopModal.jsx';
import {
  DragHandleIcon, NavigateIcon, CheckIcon, AlertIcon,
  SkipIcon, MoveIcon, PinIcon, ClockIcon,
} from '../common/icons.jsx';

const STATUS_LABELS = {
  pending: 'En attente',
  arrived: 'Arrivé',
  in_progress: 'En cours',
  delivered: 'Livré',
  skipped: 'Ignoré',
  problem: 'Problème',
};

const STATUS_BG = {
  pending: 'bg-gray-100 text-gray-600',
  arrived: 'bg-blue-100 text-[#1e40af]',
  in_progress: 'bg-orange-100 text-[#f97316]',
  delivered: 'bg-emerald-100 text-[#059669]',
  skipped: 'bg-amber-100 text-amber-700',
  problem: 'bg-red-100 text-[#dc2626]',
};

const CIRCLE_COLORS = {
  pending: 'bg-[#1e40af] text-white',
  arrived: 'bg-[#0891b2] text-white',
  in_progress: 'bg-[#f97316] text-white',
  delivered: 'bg-[#059669] text-white',
  skipped: 'bg-amber-500 text-white',
  problem: 'bg-[#dc2626] text-white',
};

const BORDER_COLORS = {
  pending: 'border-l-[#1e40af]',
  arrived: 'border-l-[#0891b2]',
  in_progress: 'border-l-[#f97316]',
  delivered: 'border-l-[#059669]',
  skipped: 'border-l-amber-500',
  problem: 'border-l-[#dc2626]',
};

/**
 * A single stop row with drag handle, status-colored border, action buttons.
 * @param {{ stop: object, index: number, planId: string, onDragStart: Function, onDragOver: Function, onDrop: Function, onStatusUpdate: Function, onMoveStop?: Function }} props
 */
export default function StopItem({ stop, index, planId, onDragStart, onDragOver, onDrop, onStatusUpdate, onMoveStop, onMoveUp, onMoveDown }) {
  const [showProblem, setShowProblem] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const navigateUrl = stop.lat && stop.lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`
    : null;

  const estimatedTime = stop.estimated_arrival
    ? new Date(stop.estimated_arrival).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : '';

  /** @param {object} problemData */
  const handleProblemSubmit = (problemData) => {
    setShowProblem(false);
    onStatusUpdate(stop.id, problemData);
  };

  return (
    <>
      <div
        draggable
        onDragStart={(e) => onDragStart(e, index)}
        onDragOver={(e) => onDragOver(e, index)}
        onDrop={(e) => onDrop(e, index)}
        className={`bg-white rounded-xl shadow-sm border-l-4 overflow-hidden ${BORDER_COLORS[stop.status]} cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow`}
      >
        <div className="flex items-start gap-2 p-3">
          {/* Drag handle + mobile reorder buttons */}
          <div className="flex-shrink-0 pt-0.5 flex flex-col items-center gap-0.5">
            <div className="hidden md:block text-gray-300 hover:text-gray-500">
              <DragHandleIcon size={18} />
            </div>
            {onMoveUp && (
              <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" aria-label="Monter">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6"/></svg>
              </button>
            )}
            {onMoveDown && (
              <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" aria-label="Descendre">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
              </button>
            )}
          </div>

          {/* Order circle */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${CIRCLE_COLORS[stop.status]}`}>
            {stop.stop_order}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-[#0f172a] truncate">{stop.client_name}</div>
            <div className="text-xs text-[#64748b] truncate flex items-center gap-1 mt-0.5">
              <PinIcon size={11} />
              {stop.address}{stop.city ? `, ${stop.city}` : ''}
            </div>
            <div className="flex flex-wrap gap-1.5 items-center mt-1.5">
              <span className="text-xs font-medium text-[#64748b] bg-gray-50 px-2 py-0.5 rounded">
                {stop.nb_units || stop.units_count} unités
              </span>
              {estimatedTime && (
                <span className="text-xs text-[#64748b] flex items-center gap-0.5">
                  <ClockIcon size={11} /> ~{estimatedTime}
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BG[stop.status]}`}>
                {STATUS_LABELS[stop.status]}
              </span>
            </div>
            {stop.problem_type && (
              <p className="text-xs text-[#dc2626] mt-1 bg-red-50 rounded px-2 py-1">
                {stop.problem_type}{stop.problem_note ? ` - ${stop.problem_note}` : ''}
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-1">
          {stop.status === 'pending' && (
            <ActionCircle color="blue" label="Arrivé"
              icon={<PinIcon size={14} />}
              onClick={() => onStatusUpdate(stop.id, { status: 'arrived', actualArrival: new Date().toISOString() })} />
          )}
          {(stop.status === 'arrived' || stop.status === 'in_progress') && (
            <ActionCircle color="green" label="Terminé"
              icon={<CheckIcon size={14} />}
              onClick={() => onStatusUpdate(stop.id, { status: 'delivered', actualDeparture: new Date().toISOString() })} />
          )}
          {stop.status !== 'delivered' && stop.status !== 'problem' && stop.status !== 'skipped' && (
            <ActionCircle color="red" label="Problème"
              icon={<AlertIcon size={14} />}
              onClick={() => setShowProblem(true)} />
          )}
          {navigateUrl && (
            <a href={navigateUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-[#64748b] bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors min-h-[36px]">
              <NavigateIcon size={14} /> GPS
            </a>
          )}
          {stop.status !== 'delivered' && stop.status !== 'skipped' && (
            <ActionCircle color="amber" label="Reporter"
              icon={<SkipIcon size={14} />}
              onClick={() => onStatusUpdate(stop.id, { status: 'skipped' })} />
          )}
          {stop.status !== 'delivered' && onMoveStop && (
            <ActionCircle color="purple" label="Déplacer"
              icon={<MoveIcon size={14} />}
              onClick={() => setShowMove(true)} />
          )}
        </div>
      </div>

      {showProblem && (
        <ProblemModal stopId={stop.id} planId={planId}
          onSubmit={handleProblemSubmit} onCancel={() => setShowProblem(false)} />
      )}
      {showMove && (
        <MoveStopModal stopId={stop.id} currentPlanId={planId}
          onMove={async (sid, targetId) => { await onMoveStop(sid, targetId); setShowMove(false); }}
          onCancel={() => setShowMove(false)} />
      )}
    </>
  );
}

/**
 * Small action button with circle icon design.
 * @param {{ color: string, label: string, icon: JSX.Element, onClick: () => void }} props
 */
function ActionCircle({ color, label, icon, onClick }) {
  const styles = {
    blue: 'text-[#1e40af] bg-blue-50 hover:bg-blue-100',
    green: 'text-[#059669] bg-emerald-50 hover:bg-emerald-100',
    red: 'text-[#dc2626] bg-red-50 hover:bg-red-100',
    amber: 'text-amber-700 bg-amber-50 hover:bg-amber-100',
    purple: 'text-purple-700 bg-purple-50 hover:bg-purple-100',
    gray: 'text-[#64748b] bg-gray-100 hover:bg-gray-200',
  };
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1 text-xs font-medium px-3 py-2 rounded-lg transition-colors min-h-[36px] ${styles[color]}`}>
      {icon} {label}
    </button>
  );
}
